import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { useAuth, useTheme, useLanguage } from '../context/AppContext';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Sun, Moon, Music, Users, Ticket, Star, ChevronRight, Menu, X } from 'lucide-react';
import SenArtisteLogo from '../components/SenArtisteLogo';
import LanguageSelector from '../components/LanguageSelector';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL + '/api';
const API_BASE = process.env.REACT_APP_BACKEND_URL;

// Helper pour résoudre les URLs d'images (relatives ou absolues)
const resolveImageUrl = (url) => {
  if (!url) return null;
  if (url.startsWith('http://') || url.startsWith('https://')) return url;
  if (url.startsWith('/api/')) return `${API_BASE}${url}`;
  if (url.startsWith('/')) return `${API_BASE}${url}`;
  return url;
};

// Placeholder images
const PLACEHOLDER_PROFILE = '/images/artist-placeholder.jpg';

const Landing = () => {
  const { user } = useAuth();
  const { theme, toggleTheme } = useTheme();
  const { t, language } = useLanguage();
  const navigate = useNavigate();
  const [artists, setArtists] = useState([]);
  const [loading, setLoading] = useState(true);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    fetchArtists();
  }, []);

  const fetchArtists = async () => {
    try {
      const response = await axios.get(`${API_URL}/artists/public`);
      setArtists(Array.isArray(response.data) ? response.data : []);
    } catch (error) {
      console.error('Fetch artists error:', error);
      setArtists([]);
    }
    setLoading(false);
  };

  const features = [
    { icon: <Music className="w-8 h-8" />, title: language === 'fr' ? "Gestion Artistique" : "Artist Management", desc: language === 'fr' ? "Gérez votre profil, médias et contenus" : "Manage your profile, media and content" },
    { icon: <Ticket className="w-8 h-8" />, title: language === 'fr' ? "Billetterie" : "Ticketing", desc: language === 'fr' ? "Vendez des billets avec QR codes" : "Sell tickets with QR codes" },
    { icon: <Users className="w-8 h-8" />, title: "Fan Base", desc: language === 'fr' ? "Connectez avec vos fans" : "Connect with your fans" },
    { icon: <Star className="w-8 h-8" />, title: t('revenue'), desc: language === 'fr' ? "Recevez 95% des ventes directement" : "Receive 95% of sales directly" },
  ];

  return (
    <div className="min-h-screen">
      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 glass">
        <div className="max-w-7xl mx-auto px-2 sm:px-3 py-1 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3" data-testid="logo-link">
            <SenArtisteLogo className="w-16 h-16 sm:w-20 sm:h-20" />
            <span className="font-syne font-bold text-xl sm:text-2xl">Sen Artiste</span>
            <LanguageSelector className="ml-1" />
          </Link>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-4">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            
            <Link to="/about">
              <Button variant="ghost" data-testid="about-btn">
                {t('about')}
              </Button>
            </Link>
            
            <Link to="/retrouver-billets">
              <Button variant="ghost" data-testid="find-tickets-btn">
                <Ticket className="w-4 h-4 mr-2" />
                {language === 'fr' ? 'Retrouver mes billets' : 'Find my tickets'}
              </Button>
            </Link>
            
            {user ? (
              <Button 
                onClick={() => {
                  if (user.role === 'SUPER_ADMIN') navigate('/admin');
                  else if (user.role === 'APPORTEUR') navigate('/apporteur/dashboard');
                  else if (user.role === 'SCANNER') navigate(user.artistSlug ? `/${user.artistSlug}/scanner` : '/login');
                  else if (user.artistSlug) navigate(`/${user.artistSlug}/management`);
                  else navigate('/login');
                }}
                className="btn-primary"
                data-testid="dashboard-btn"
              >
                {t('dashboard')}
              </Button>
            ) : (
              <div className="flex gap-2">
                <Button variant="ghost" onClick={() => navigate('/login')} data-testid="login-btn">
                  {t('login')}
                </Button>
                <Button onClick={() => navigate('/register')} className="btn-primary" data-testid="register-btn">
                  {t('register')}
                </Button>
              </div>
            )}
          </div>

          {/* Mobile Menu Button */}
          <div className="flex md:hidden items-center gap-2">
            <Button
              variant="ghost"
              size="icon"
              onClick={toggleTheme}
              data-testid="theme-toggle-mobile"
            >
              {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
            </Button>
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              data-testid="mobile-menu-btn"
            >
              {mobileMenuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
            </Button>
          </div>
        </div>
      </nav>

      {/* Mobile Menu Overlay - Portaled outside nav for better z-index */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-[9999]">
          {/* Dark Overlay */}
          <div 
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => setMobileMenuOpen(false)}
          />
          {/* Menu Panel - Slide from right */}
          <div 
            className="absolute top-0 right-0 h-full w-[280px] max-w-[85vw] bg-background shadow-2xl border-l border-border flex flex-col"
            style={{ 
              animation: 'slideInRight 0.3s ease-out',
            }}
          >
            {/* Header with close button */}
            <div className="flex items-center justify-between p-4 border-b border-border">
              <span className="font-syne font-bold text-lg">Menu</span>
              <button 
                onClick={() => setMobileMenuOpen(false)}
                className="p-2 hover:bg-muted rounded-full transition-colors"
                aria-label="Fermer le menu"
              >
                <X className="w-6 h-6" />
              </button>
            </div>
            
            {/* Menu Items */}
            <div className="flex-1 overflow-y-auto p-4 space-y-2">
              <Link to="/about" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start text-base h-12" data-testid="about-btn-mobile">
                  {t('about')}
                </Button>
              </Link>
              
              <Link to="/retrouver-billets" onClick={() => setMobileMenuOpen(false)}>
                <Button variant="ghost" className="w-full justify-start text-base h-12" data-testid="find-tickets-btn-mobile">
                  <Ticket className="w-5 h-5 mr-3" />
                  {language === 'fr' ? 'Retrouver mes billets' : 'Find my tickets'}
                </Button>
              </Link>
              
              <div className="border-t border-border my-4" />
              
              {user ? (
                <Button 
                  onClick={() => {
                    setMobileMenuOpen(false);
                    if (user.role === 'SUPER_ADMIN') navigate('/admin');
                    else if (user.role === 'APPORTEUR') navigate('/apporteur/dashboard');
                    else if (user.role === 'SCANNER') navigate(user.artistSlug ? `/${user.artistSlug}/scanner` : '/login');
                    else if (user.artistSlug) navigate(`/${user.artistSlug}/management`);
                    else navigate('/login');
                  }}
                  className="btn-primary w-full h-12 text-base"
                  data-testid="dashboard-btn-mobile"
                >
                  {t('dashboard')}
                </Button>
              ) : (
                <div className="space-y-3">
                  <Button 
                    variant="outline"
                    className="w-full h-12 text-base"
                    onClick={() => { setMobileMenuOpen(false); navigate('/login'); }} 
                    data-testid="login-btn-mobile"
                  >
                    {t('login')}
                  </Button>
                  <Button 
                    className="btn-primary w-full h-12 text-base"
                    onClick={() => { setMobileMenuOpen(false); navigate('/register'); }} 
                    data-testid="register-btn-mobile"
                  >
                    {t('register')}
                  </Button>
                </div>
              )}
            </div>
            
            {/* Footer */}
            <div className="p-4 border-t border-border">
              <p className="text-xs text-muted-foreground text-center">Sen Artiste © 2024</p>
            </div>
          </div>
        </div>
      )}

      {/* Hero Section */}
      <section className="relative min-h-screen flex items-center justify-center pt-20 hero-gradient noise-overlay overflow-hidden">
        <div 
          className="absolute inset-0 opacity-20"
          style={{
            backgroundImage: 'url(/images/stage-bg.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        <div className="relative z-10 max-w-7xl mx-auto px-6 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
          >
            <span className="accent-text text-primary mb-4 block">{t('heroTitle')}</span>
            <h1 className="font-syne text-5xl sm:text-6xl lg:text-7xl font-extrabold mb-6 leading-tight">
              {t('connectWithFans')} <br /><span className="text-primary">Fans</span> {t('worldwide')}
            </h1>
            <p className="font-manrope text-lg sm:text-xl text-muted-foreground max-w-2xl mx-auto mb-10">
              {t('heroSubtitle')}
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="btn-primary text-lg px-8 py-6 glow-gold"
                onClick={() => navigate('/devenir-artiste')}
                data-testid="cta-artist"
              >
                {t('becomeArtist')}
                <SenArtisteLogo className="ml-2 w-5 h-5" />
              </Button>
              <Button 
                size="lg" 
                variant="outline" 
                className="text-lg px-8 py-6"
                onClick={() => {
                  const element = document.getElementById('artists');
                  element?.scrollIntoView({ behavior: 'smooth' });
                }}
                data-testid="cta-discover"
              >
                {t('discoverArtists')}
              </Button>
            </div>
          </motion.div>
        </div>
        
        {/* Scroll indicator */}
        <motion.div 
          className="absolute bottom-10 left-1/2 -translate-x-1/2"
          animate={{ y: [0, 10, 0] }}
          transition={{ repeat: Infinity, duration: 2 }}
        >
          <div className="w-6 h-10 border-2 border-primary/50 rounded-full flex justify-center pt-2">
            <div className="w-1.5 h-3 bg-primary rounded-full" />
          </div>
        </motion.div>
      </section>

      {/* Features Section */}
      <section className="py-24 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="accent-text text-primary">{t('whySenArtiste')}</span>
            <h2 className="font-syne text-4xl font-bold mt-4">{t('everythingYouNeed')}</h2>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card className="glass-card card-hover h-full" data-testid={`feature-card-${index}`}>
                  <CardContent className="p-8 text-center">
                    <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/10 flex items-center justify-center text-primary">
                      {feature.icon}
                    </div>
                    <h3 className="font-syne font-bold text-xl mb-3">{feature.title}</h3>
                    <p className="text-muted-foreground">{feature.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Artists Section */}
      <section id="artists" className="py-24 px-6 bg-secondary/30">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <span className="accent-text text-primary">{t('ourArtists')}</span>
            <h2 className="font-syne text-4xl font-bold mt-4">{t('discoverTalents')}</h2>
          </div>
          
          {loading ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {[1, 2, 3].map((i) => (
                <Card key={i} className="glass-card animate-pulse">
                  <div className="h-64 bg-muted" />
                  <CardContent className="p-6">
                    <div className="h-6 bg-muted rounded w-3/4 mb-2" />
                    <div className="h-4 bg-muted rounded w-1/2" />
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : artists.length > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
              {artists.map((artist, index) => (
                <motion.div
                  key={artist.id}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  viewport={{ once: true }}
                >
                  <Link to={`/${artist.slug}`} data-testid={`artist-card-${artist.slug}`}>
                    <Card className="glass-card card-hover overflow-hidden group">
                      <div className="relative h-64 overflow-hidden">
                        <img 
                          src={resolveImageUrl(artist.profilePhotoUrl) || PLACEHOLDER_PROFILE} 
                          alt={artist.nomDeScene}
                          className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                          onError={(e) => { if (e.target.src !== PLACEHOLDER_PROFILE) e.target.src = PLACEHOLDER_PROFILE; }}
                        />
                        <div className="absolute inset-0 bg-gradient-to-t from-black/80 to-transparent" />
                        <div className="absolute bottom-4 left-4 right-4">
                          <h3 className="font-syne font-bold text-2xl text-white">{artist.nomDeScene}</h3>
                          <p className="text-white/70">{artist.prenom} {artist.nom}</p>
                        </div>
                      </div>
                      <CardContent className="p-4">
                        <p className="text-muted-foreground line-clamp-2">{artist.bio || t('talentedArtist')}</p>
                        <div className="mt-4 flex items-center text-primary font-medium">
                          {t('viewFanpage')}
                          <ChevronRight className="w-4 h-4 ml-1" />
                        </div>
                      </CardContent>
                    </Card>
                  </Link>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16" data-testid="no-artists">
              <p className="text-muted-foreground mb-4">Aucun artiste disponible pour le moment.</p>
              <Button onClick={() => navigate('/devenir-artiste')} data-testid="become-artist-empty-btn">
                Devenir le premier artiste
              </Button>
            </div>
          )}
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-6 relative overflow-hidden">
        <div 
          className="absolute inset-0 opacity-10"
          style={{
            backgroundImage: 'url(/images/concert-bg.jpg)',
            backgroundSize: 'cover',
            backgroundPosition: 'center',
          }}
        />
        
        <div className="relative z-10 max-w-4xl mx-auto text-center">
          <h2 className="font-syne text-4xl sm:text-5xl font-bold mb-6">
            Prêt à rejoindre <span className="text-primary">Sen Artiste</span> ?
          </h2>
          <p className="text-lg text-muted-foreground mb-10 max-w-2xl mx-auto">
            Rejoignez des centaines d'artistes qui utilisent déjà notre plateforme pour 
            connecter avec leurs fans et gérer leur carrière.
          </p>
          <Button 
            size="lg" 
            className="btn-primary text-lg px-10 py-6 glow-gold"
            onClick={() => navigate('/register')}
            data-testid="cta-final"
          >
            Créer mon compte artiste
          </Button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-border">
        <div className="max-w-7xl mx-auto flex flex-col md:flex-row justify-between items-center gap-6">
          <div className="flex items-center gap-3">
            <SenArtisteLogo className="w-10 h-10" />
            <span className="font-syne font-bold text-xl">Sen Artiste</span>
          </div>
          
          <p className="text-muted-foreground text-sm">
            © 2025 Sen Artiste. {t('allRightsReserved')}.
          </p>
          
          <div className="flex gap-6">
            <Link to="/about" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              {t('about')}
            </Link>
            <Link to="/login" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Connexion
            </Link>
            <Link to="/admin" className="text-sm text-muted-foreground hover:text-primary transition-colors">
              Admin
            </Link>
          </div>
        </div>
        
        {/* Developed by */}
        <div className="max-w-7xl mx-auto mt-8 pt-6 border-t border-border/50 text-center">
          <p className="text-sm text-muted-foreground">
            Développé avec ❤️ par{' '}
            <Link to="/about" className="text-primary hover:underline font-medium">
              DKR Consulting
            </Link>
          </p>
        </div>
      </footer>
    </div>
  );
};

export default Landing;
